<template>
    <div>
        <v-server-table :name="tableName" :ref="tableName" url="/subcontractors/list" :columns="columns" :options="options">

            <div slot="afterFilterWrapper" class="ml-md-2 ml-0 mt-md-0 mt-2 mb-md-0 mb-3 float-md-left">
                <div class="clearfix d-md-none d-block"></div>

                <div v-if="allowFilterSaving" class="float-left mr-1">
                    <user-filters ref="subSavedFilters" assetType="Subcontractor" :filterKey="filterKey" @saving="setUserFilterData" @show="setUserFilterData"></user-filters>
                </div>

                <div class="float-left mr-2">
                    <table-filters-2 ref="subTableFilters"
                        filterUrl="/subcontractors/filters"
                        v-bind:filters="filters"
                        :emptyFilters="filterDefs"
                        :filterKey="filterKey"
                        @filtersChanged="refreshTableData"
                        @filtersCleared="filtersCleared"
                        v-show="!dashboardFilters">
                        <template slot-scope="slotProps">
                            <div class="d-inline-block">
                                <multi-select v-model="slotProps.filters['subcontractors.status_id']" :btnLabel="(values) => ('Statuses ('+values.length+')')" :selectOptions="slotProps.filterData.statuses" :options="{multi: true}"></multi-select>
                            </div>
                            <div class="d-inline-block">
                                <date-range-selector buttonTitle="Approved Date" v-model="slotProps.filters['subcontractors.valid_until']" />
                            </div>
                        </template>
                    </table-filters-2>
                </div>

                <button type="button" class="btn btn-light" v-on:click="exportSubcontractors()"><i class="fa fa-file-excel-o"></i> Export</button>

                <router-link to="subcontractors/statuses" class="btn btn-light" target="_blank" title="Status Help">
                    <i class="fa fa-life-ring"></i> Status
                </router-link>

                <div class="clearfix d-md-none d-block"></div>
            </div>

            <router-link slot="subcontractor_name" slot-scope="props" :to="`/subcontractors/view/${props.row.subcontractor_id}`" title="View Subcontractor">
                {{ props.row.subcontractor_name }}
            </router-link>

            <template slot="status_name" slot-scope="props">
                <template v-if="props.row.status_name">
                    <span :title="props.row.status_desc" :class="`badge badge-${props.row.status_color}`">{{ props.row.status_name }}</span>
                </template>
            </template>

            <template slot="has_previous_projects" slot-scope="props">
                {{ props.row.has_previous_projects === null ? '' : (props.row.has_previous_projects ? 'Yes' : 'No') }}
            </template>

            <template slot="created_at" slot-scope="props">
                {{ props.row.created_at | moment("MM/DD/YYYY") }}
            </template>

            <template slot="updated_at" slot-scope="props">
                {{ props.row.updated_at | moment("MM/DD/YYYY") }}
            </template>

            <template slot="valid_until" slot-scope="props">
                <span v-if="props.row.valid_until">{{ props.row.valid_until | moment("MM/DD/YYYY") }}</span>
            </template>

            <template slot="average_rating" slot-scope="props">
                {{ props.row.average_rating ? (+props.row.average_rating).toFixed(1) : '-' }}
            </template>

            <template v-for="field in custom_fields" :slot="`custom_field_value_${field.id}`" slot-scope="props">
                {{ renderCustomFieldValue(field, props.row['custom_field_value_'+field.id]) }}
            </template>

        </v-server-table>
    </div>
</template>

<script>
    import filterExtractor from '@/helpers/TableFilterExtractor';
    import customVTColDropdown from '@/components/vue-tables-2/CustomColumnDropdown';

    export default {
        props: {
            assetType: {
                type: String,
                default: null,
            },

            assetId: {
                type: Number,
                default: null,
            },

            allowFilterSaving: {
                type: Boolean,
                default: false,
            },

            filterId: {
                type: Number,
                default: null,
            },

            custom_fields: {
                type: Array,
                default(){
                    return [];
                },
            },

            dashboardFilters: {
                type: Object,
                default(){
                    return null;
                },
            },

            tableConfig: {
                type: Object,
                default(){
                    return null;
                },
            }
        },

        data() {
            return {
                has_loaded: false,
                tableName: 'subcontractorTable',

                filterDefs: {
                    'subcontractors.valid_until': null,
                    'subcontractors.status_id': [],
                },

                filterKey: 'subcontractorFilters',
            }
        },

        watch: {
            appliedFilter(newVal, oldVal){
                //Call the helper
                this.checkForAppliedFilter();
            },
        },

        computed: {
            columns() {
                let columns = [
                    'subcontractor_name',
                    'status_name',
                    'trades',
                    'city',
                    'state',
                    'website',
                    'company_contact_email',
                    'designations',
                    'areas',
                    'company_id_num',
                    'has_previous_projects',
                    'created_at',
                    'updated_at',
                    'valid_until',
                    'average_rating'
                ];

                if (this.assetType == 'Expiring')
                {
                    columns.push('days_remaining');
                }

                //Check for custom fields
                for (var i = 0; i < this.custom_fields.length; i++)
                {
                    columns.push('custom_field_value_'+this.custom_fields[i].id);
                }

                return columns;
            },

            filters(){
                //Check State
                if(this.$store.state.tableFilters.filters[this.filterKey])
                {
                    //Send it
                    return this.$store.state.tableFilters.filters[this.filterKey];
                }

                //Return Definition
                return JSON.parse(JSON.stringify(this.filterDefs));
            },

            appliedFilter(){
                //Send it
                return this.$store.state.appliedFilters[this.filterKey] ? this.$store.state.appliedFilters[this.filterKey] : null;
            },

            options(){
                //SEtup
                var headings = {
                    subcontractor_name: 'Name',
                    status_name: 'Status',
                    trades: 'Trade',
                    city: 'City',
                    state: 'State',
                    created_at: 'Created At',
                    updated_at: 'Last Updated',
                    website: 'Website',
                    company_contact_email: 'Contact Email',
                    designations: 'Designations',
                    areas: 'Working Areas',
                    company_id_num: 'Federal ID/SSN',
                    has_previous_projects: 'Performed Past Work',
                    created_at: 'Created At',
                    updated_at: 'Last Updated',
                    valid_until: 'Approved Until',
                    average_rating: 'Overall Rating',
                    days_remaining: 'Days Remaining',
                };

                //Check for custom fields
                for (var i = 0; i < this.custom_fields.length; i++)
                {
                    headings['custom_field_value_'+this.custom_fields[i].id] = this.custom_fields[i].label;
                }

                //Send it
                return {
                    headings: headings,
                    saveState: this.allowFilterSaving,
                    columnsDropdown: customVTColDropdown,
                    visibleColumns: (this.allowFilterSaving ? this.$root.getVisibleColumnsFromState(this.tableName) : false),
                    sendInitialRequest: (this.filterId ? false : true),
                    orderBy: {
                        ascending: true,
                        column: 'subcontractors.subcontractor_name',
                    },
                    saveState: this.allowFilterSaving,
                    responseAdapter: (response) => {
                        //Emit the count
                        this.$emit('subcontractorCount', response.data.count);

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Set the filters
                        //data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));
                        data.filters = (this.dashboardFilters ? filterExtractor.extractFilters(this.dashboardFilters) : filterExtractor.extractFilters(this.filters));

                        //INclude the asset
                        data.assetType = this.assetType;
                        data.assetId = this.assetId;

                        //Check for Custom Fields
                        if( this.custom_fields.length > 0)
                        {
                            //Include Custom Fields
                            data.load_custom_fields = true;
                        }

                        //Send it
                        return data;
                    },
                    uniqueKey: 'subcontractor_id',
                    saveState: true,
                    storage: 'local',
                };
            },
        },

        methods: {

            exportSubcontractors(){
                //Loading indicator
                this.exportingTable = true;

                //Build the payload
                var payload = {
                    // filters: this.$store.state.tableFilters.subcontractorFilters,
                    filters: filterExtractor.extractFilters(this.filters),
                    query: (this.$refs.subcontractorTable.getRequestParams()).query,
                    orderBy: this.$refs.subcontractorTable.options.orderBy.column,
                    ascending: this.$refs.subcontractorTable.options.orderBy.ascending,
                    table: 'subcontractor-table',
                    columns: this.$refs.subcontractorTable.columns,
                    visible_columns: this.$root.getVisibleColumnsFromState(this.tableName),
                };

                //Send it
                this.$http.post('/subcontractors/export', payload)
                    .then(response => {
                        //Open the new window
                        window.open(response.data);
                    })
                    .catch(err => {})
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },

            refreshTableData(filters) {
                //Set the data
                this.$refs.subcontractorTable.setPage(1);
                //this.$refs.subcontractorTable.getData();
            },

            filtersCleared(){
                //Reset
                this.refreshTableData();

                //Reset the Columns
                this.$refs.subTableFilters.resetColVis(this.$refs.subcontractorTable);
            },

            setUserFilterData(){
                //Call the Helper
                this.$refs.subSavedFilters.setFilterParams(this.$refs.subcontractorTable);
            },

            checkForAppliedFilter(){
                //Check if
                if(!this.allowFilterSaving || !this.appliedFilter)
                {
                    return;
                }

                //Call the helper
                this.$refs.subTableFilters.setColVis(this.$refs.subcontractorTable);
            },

            renderCustomFieldValue(field, value){
                //Setup
                var display = '';

                //Parse the Value
                value = JSON.parse(value);

                //Handle based on type
                switch(field.type)
                {
                    case 'Radio':
                        //Loop and Get the Options
                        for (var i = 0; i < field.field_options.length; i++)
                        {
                            //Check the ID
                            if(field.field_options[i].id === parseInt(value))
                            {
                                display = field.field_options[i].label;
                            }
                        }
                        break;

                    case 'Dropdown':
                    case 'Checkbox':
                        //Check if Item is array or not
                        value = !Array.isArray(value) ? [value] : value;

                        //Loop and Get the Options
                        for (var i = 0; i < field.field_options.length; i++)
                        {
                            for (var j = 0; j < value.length; j++)
                            {
                                //Check the ID
                                if(field.field_options[i].id === parseInt(value[j]))
                                {
                                    display += (display != '' ? ', ' : '') + field.field_options[i].label;
                                }
                            }
                        }
                        break;

                    default:
                        //SEnd the value
                        display = value;
                        break;
                }

                //Send it
                return display;
            },
        },

        mounted() {
            //Check for Staring Filters
            if(!this.dashboardFilters)
            {
                //Check for a saved filter
                this.checkForAppliedFilter();

                //Check for Filter ID
                if(this.filterId)
                {
                    this.$refs.subSavedFilters.setSavedFilter(this.filterId);
                }
            }

            //Check for Table Config
            if(this.tableConfig)
            {
                //Set it
                this.$refs.subTableFilters.setColVisFromSavedFilter(this.$refs.subcontractorTable, this.tableConfig);
            }
        },
    }
</script>
